import React from "react";
import { Button, Divider, Icon, Image } from "semantic-ui-react";
import { ensureHttpsImageUrl } from "../../../../app/common/util/util";
import { NAVY, BLUE, GREY_TEXT, LIGHT_BLUE_BG, LIGHT_BLUE_BORDER, REPORT_STATUS_LABELS, STAGE_STATUS_DISPLAY, isResearchFinished, researchStages } from "./piModel";

// Indeterminate bar animation (used until the research plan - and so a real
// percentage - exists). Scoped class name; no global styling is changed.
const INDETERMINATE_CSS = `@keyframes piIndeterminate { 0% { left: -35%; } 100% { left: 100%; } }
.pi-progress-indeterminate { position: absolute; top: 0; bottom: 0; width: 35%; border-radius: 999px; background: ${BLUE}; animation: piIndeterminate 1.4s ease-in-out infinite; }`;

function ProgressBar({ percent, failed }) {
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent ?? undefined}
      aria-label="Research progress"
      style={{ position: "relative", height: 8, borderRadius: 999, background: "#eef1f5", overflow: "hidden" }}
    >
      {percent === null ? (
        <div className="pi-progress-indeterminate" />
      ) : (
        <div style={{ width: `${percent}%`, height: "100%", borderRadius: 999, background: failed ? "#f2711c" : BLUE, transition: "width 0.4s ease" }} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const d = STAGE_STATUS_DISPLAY[status];
  return (
    <span style={{ display: "inline-block", whiteSpace: "nowrap", background: d.bg, color: d.color, borderRadius: 999, padding: "3px 10px", fontSize: "0.8em", fontWeight: 600 }}>
      {d.label}
    </span>
  );
}

function StageRow({ stage, last }) {
  const d = STAGE_STATUS_DISPLAY[stage.status];
  const active = stage.status === "in_progress";
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: last ? "none" : "1px solid #eef1f5" }}>
      <Icon name={d.icon} loading={active} style={{ color: d.color, fontSize: "1.25em", margin: "2px 0 0", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: NAVY }}>{stage.title}</div>
        <div style={{ color: GREY_TEXT, fontSize: "0.88em", marginTop: 2 }}>
          {stage.description}
          {stage.moduleCount > 1 && stage.status === "in_progress" ? ` · ${stage.doneCount} of ${stage.moduleCount} done` : ""}
        </div>
      </div>
      <StatusPill status={stage.status} />
    </div>
  );
}

/**
 * Compact research-progress view (shown before the report is opened).
 * Everything comes from persisted backend state; closing it never affects
 * the research job.
 */
export default function PiResearchProgress({ transaction, report, modules, resuming, onResume, onViewReport, onClose, isMobile }) {
  const address = report?.propertyAddress || transaction.address || {};
  const status = report?.status || "preparing";
  const finished = isResearchFinished(status);
  const { stages, percent, anyFailed } = researchStages(modules, status);
  const needsResume = !finished && (status === "failed_recoverable" || (anyFailed && !stages.some((s) => s.status === "in_progress")));

  const heading = finished ? "Your report is ready" : needsResume ? "Research needs attention" : "Researching your property";
  const description = finished
    ? status === "partially_completed"
      ? "Some research areas couldn't be completed. They're clearly marked in the report - nothing was assumed about them."
      : "Your Property Intelligence report has been saved to this transaction."
    : needsResume
      ? "Some research steps didn't complete. Resuming continues the same research - completed results are kept and no additional Strategy Credit is used."
      : "We're reviewing property records, official sources, and neighborhood information to prepare your Property Intelligence report.";

  return (
    <div style={{ padding: isMobile ? "4px 2px" : "8px 8px 4px" }}>
      <style>{INDETERMINATE_CSS}</style>

      {/* Title row - right padding keeps clear of the modal close icon */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", paddingRight: 36, marginBottom: 16 }}>
        <Image src="/assets/TA-Logo-Pro.png" alt="TransActioner Pro" style={{ height: 60, width: "auto" }} />
        <span style={{ color: NAVY, fontWeight: 800, fontSize: "1.35em" }}>Property Intelligence</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Image
          src={transaction?.pic ? ensureHttpsImageUrl(transaction.pic) : "/assets/placeholder-house.png"}
          alt=""
          style={{ width: isMobile ? 72 : 88, height: isMobile ? 56 : 68, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: "1.1em", overflowWrap: "anywhere" }}>
            {address.street}{address.unit ? ` ${address.unit}` : ""}
          </div>
          <div style={{ color: "#5a6472" }}>{[address.city, [address.state, address.zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
          <div style={{ marginTop: 4 }}>
            <StatusPill status={finished ? (status === "completed" ? "complete" : "partial") : needsResume ? "failed" : "in_progress"} />
            <span style={{ color: GREY_TEXT, fontSize: "0.8em", marginLeft: 8 }}>{REPORT_STATUS_LABELS[status] || "Starting"}</span>
          </div>
        </div>
      </div>

      <Divider style={{ margin: "18px 0" }} />

      <div style={{ color: NAVY, fontWeight: 700, fontSize: "1.12em" }}>{heading}</div>
      <p style={{ color: GREY_TEXT, margin: "4px 0 14px", lineHeight: 1.5 }}>{description}</p>
      <ProgressBar percent={percent} failed={needsResume} />
      {percent !== null && !finished && (
        <div style={{ color: GREY_TEXT, fontSize: "0.8em", marginTop: 4, textAlign: "right" }}>{percent}% of research steps finished</div>
      )}

      <div style={{ marginTop: 6 }}>
        {stages.map((stage, i) => <StageRow key={stage.key} stage={stage} last={i === stages.length - 1} />)}
      </div>

      {!finished && (
        <div style={{ display: "flex", gap: 10, background: LIGHT_BLUE_BG, border: `1px solid ${LIGHT_BLUE_BORDER}`, borderRadius: 10, padding: "12px 14px", marginTop: 14, fontSize: "0.92em", color: NAVY }}>
          <Icon name="info circle" style={{ color: BLUE, margin: "2px 0 0" }} />
          <div>
            {needsResume
              ? "Completed results are saved. You can close this window and resume later from Property Intelligence. No additional Strategy Credit is required."
              : "You can close this window and return later. Your research will continue and your report will be saved automatically. No additional Strategy Credit is required."}
          </div>
        </div>
      )}

      {/* Primary action is last on desktop (right-aligned) and first on mobile (stacked). */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column-reverse" : "row", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        {finished ? (
          <>
            <Button basic onClick={onClose} content="Close" fluid={isMobile} />
            <Button primary onClick={onViewReport} content="View Property Intelligence Report" fluid={isMobile} />
          </>
        ) : needsResume ? (
          <>
            <Button basic onClick={onClose} content="Close" disabled={resuming} fluid={isMobile} />
            <Button primary icon="redo" loading={resuming} disabled={resuming} onClick={onResume} content="Resume Research" fluid={isMobile} />
          </>
        ) : (
          <Button primary onClick={onClose} content="Continue Working" fluid={isMobile} />
        )}
      </div>
    </div>
  );
}
